import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { DraftCommentProvider } from '@sketch/modules-common'
import { AnnotationOverlayContext } from '../../../../annotations/components'
import {
  useLockCamera,
  useGetNodeAtPosition,
  useMapRelativePositionToCameraPosition,
  usePRFileArtboards,
  usePRFileSections,
  useStatus,
  PRMarinaNode,
} from '@sketch-hq/sketch-web-renderer'
import { MouseCoordinates } from '../../../../annotations/types'
import { AnnotationDotFragment } from '@sketch/gql-types'
import { useHideAnnotationDots } from '../../../../annotations/hooks'
import { getAnnotatableNode } from '../../../../annotations/utils'

interface PageAnnotationsOverlayContextProps
  extends Pick<
    React.ComponentProps<typeof AnnotationOverlayContext>,
    'disabled' | 'children' | 'hidden'
  > {
  isViewingLatestVersion: boolean
  permanentPageId: string
  /**
   * When set (single-Section focus view), only annotations belonging to this
   * Section are rendered - not every annotation on the page. Leave undefined in
   * the full page view to keep showing all of the page's annotations.
   */
  sectionUUID?: string
}

const PageAnnotationsOverlayContext = (
  props: PageAnnotationsOverlayContextProps
) => {
  const { permanentPageId, hidden, sectionUUID, ...otherProps } = props

  const fileSections = usePRFileSections()
  const fileArtboards = usePRFileArtboards()

  // The entities visible on the canvas when focusing a Section: the Section
  // itself plus every Section and frame nested inside it (it is isolated as the
  // scene root, so its descendants render within it). An annotation is shown
  // when the entity it is anchored to - a Section or a frame belonging to one
  // of these Sections - is within this subtree. Undefined in the full page
  // view, where every annotation is shown.
  const visibleEntityUUIDs = useMemo(() => {
    if (!sectionUUID) {
      return undefined
    }

    const visible = new Set<string>([sectionUUID])

    const addIfInsideFocusedSection = (node: PRMarinaNode | null) => {
      for (
        let ancestor = node?.getParentNode();
        ancestor;
        ancestor = ancestor.getParentNode()
      ) {
        if (ancestor.getLayerUUID() === sectionUUID) {
          const uuid = node?.getLayerUUID()
          if (uuid) {
            visible.add(uuid)
          }
          return
        }
      }
    }

    for (const section of fileSections ?? []) {
      addIfInsideFocusedSection(section.getNode())
    }
    for (const artboard of fileArtboards ?? []) {
      addIfInsideFocusedSection(artboard.getNode())
    }

    return visible
  }, [fileSections, fileArtboards, sectionUUID])

  const status = useStatus()
  const lockCamera = useLockCamera()
  const [lockCameraState, setLockCameraState] = useState(false)

  useEffect(() => {
    lockCamera(lockCameraState)
  }, [lockCamera, lockCameraState])

  const getNodeAtPosition = useGetNodeAtPosition()
  const mapRelativePosition = useMapRelativePositionToCameraPosition()

  const getElementAtPosition = useCallback(
    async (mouseCoordinates: MouseCoordinates) => {
      const node = await getNodeAtPosition(
        mouseCoordinates.clientX,
        mouseCoordinates.clientY
      )
      const annotatable = getAnnotatableNode(node)

      if (annotatable?.type === 'ARTBOARD') {
        return {
          type: 'ARTBOARD' as const,
          permanentId: annotatable.node.getLayerUUID(),
          bounds: annotatable.node.getAbsoluteBounds(),
          permanentPageId,
        }
      }

      if (annotatable?.type === 'SECTION') {
        return {
          type: 'SECTION' as const,
          permanentId: annotatable.node.getLayerUUID(),
          bounds: annotatable.node.getAbsoluteBounds(),
          permanentPageId,
        }
      }

      return {
        type: 'PAGE' as const,
        permanentId: permanentPageId,
      }
    },
    [getNodeAtPosition, permanentPageId]
  )

  const getPositionRelativeToOrigin = useCallback(
    (mouseCoordinates: MouseCoordinates) => {
      if (status.type !== 'READY') {
        return null
      }

      return mapRelativePosition(
        mouseCoordinates.clientX,
        mouseCoordinates.clientY
      )
    },
    [mapRelativePosition, status]
  )

  const shouldRenderAnnotation = useCallback(
    (annotationDot: AnnotationDotFragment) => {
      const { currentSubject } = annotationDot

      const isOnThisPage =
        currentSubject.__typename === 'Artboard'
          ? currentSubject.page?.uuid === permanentPageId
          : currentSubject.__typename === 'Page'
            ? currentSubject.pageUUID === permanentPageId
            : false

      if (!isOnThisPage) {
        return false
      }

      // In the single-Section focus view, restrict to annotations anchored to an
      // entity currently on the canvas - the focused Section, one of its nested
      // Sections, or a frame belonging to any of them. Otherwise every annotation
      // on the page (including ones over other, now-hidden Sections) would render.
      if (sectionUUID) {
        const anchorUUID =
          currentSubject.__typename === 'Artboard'
            ? currentSubject.uuid
            : annotationDot.sectionUuid
        return anchorUUID
          ? (visibleEntityUUIDs?.has(anchorUUID) ?? false)
          : false
      }

      return true
    },
    [permanentPageId, sectionUUID, visibleEntityUUIDs]
  )

  const [hideAnnotationsDots] = useHideAnnotationDots()
  return (
    <DraftCommentProvider>
      <AnnotationOverlayContext
        onMovingAnnotation={setLockCameraState}
        getPositionRelativeToOrigin={getPositionRelativeToOrigin}
        getElementAtPosition={getElementAtPosition}
        hidden={hideAnnotationsDots || hidden}
        shouldRenderAnnotation={shouldRenderAnnotation}
        {...otherProps}
      />
    </DraftCommentProvider>
  )
}

export default PageAnnotationsOverlayContext
